/**
 * Tiptap Direct Upload Module
 * 
 * This module provides direct upload functionality for the Tiptap editor,
 * uploading files directly to Azure Blob Storage instead of through the server.
 */

import { DirectUpload } from '@rails/activestorage';
import { mergeBlockIndentAttrs } from '../lib/post_block_indent.js';

const DIRECT_UPLOAD_URL = '/rails/active_storage/direct_uploads';

/**
 * Upload a file directly to Azure and return the URL
 * 
 * @param {File} file - The file to upload
 * @param {Object} options - Options
 * @param {Function} options.onProgress - Progress callback (receives 0-100)
 * @returns {Promise<Object>} - { url, signed_id, filename, content_type }
 */
export async function uploadToAzure(file, options = {}) {
  // Import chunked upload function - automatically handles chunking for large files
  const { uploadFileWithChunking } = await import('./azure_chunked_upload.js');
  
  try {
    console.log('[DirectUpload] Starting upload (chunked if > 256MB)...');
    
    const result = await uploadFileWithChunking(file, {
      onProgress: options.onProgress
    });

    console.log('[DirectUpload] Upload successful:', result);

    return {
      url: result.display_url || result.url,
      signed_id: result.signed_id,
      filename: result.filename,
      content_type: result.content_type,
      byte_size: result.byte_size,
      display_url: result.display_url
    };
  } catch (error) {
    console.error('[DirectUpload] Upload error:', error);
    throw error;
  }
}


/**
 * Fetch the signed URL for a blob
 * 
 * @param {string} signedId - The signed ID of the blob
 * @returns {Promise<string>} - The signed URL
 */
async function fetchSignedUrl(signedId) {
  const csrfToken = document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || '';
  
  // Use query parameter to avoid URL encoding issues with Base64 signed_id
  const response = await fetch(`/signed_blob_urls?signed_id=${encodeURIComponent(signedId)}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken
    }
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    console.error('[DirectUpload] Failed to fetch signed URL:', response.status, errorText);
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  const data = await response.json();
  
  if (!data.url) {
    console.error('[DirectUpload] Response missing URL:', data);
    throw new Error('Response missing URL');
  }
  
  console.log('[DirectUpload] Got signed URL:', data.url);
  return data.url;
}

/**
 * Fallback: Fetch blob URL via legacy direct_uploads endpoint
 * 
 * @param {string} signedId - The signed ID of the blob
 * @returns {Promise<Object>} - { url }
 */
async function fetchLegacyBlobUrl(signedId) {
  const csrfToken = document.querySelector('meta[name=csrf-token]')?.getAttribute('content') || '';
  
  // Use the signed_blob_urls index endpoint as fallback (with signed_id param)
  const response = await fetch(`/signed_blob_urls?signed_id=${encodeURIComponent(signedId)}`, {
    method: 'GET',
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken
    }
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const data = await response.json();
  return data;
}

/**
 * Create a file upload handler function for Tiptap that uses direct upload
 * 
 * This returns a function that can be used as the onFileUpload callback
 * in TiptapEditorComponent or TiptapToolbar.
 * 
 * @param {Object} editor - The Tiptap editor instance
 * @param {Object} options - Options
 * @param {Function} options.onStart - Called when upload starts
 * @param {Function} options.onProgress - Progress callback
 * @param {Function} options.onComplete - Called when upload completes
 * @param {Function} options.onError - Called on error
 * @returns {Function} - File upload handler function
 */
export function createTiptapUploadHandler(editor, options = {}) {
  return async (file) => {
    if (!editor) return;

    const { onStart, onProgress, onComplete, onError } = options;
    
    if (onStart) onStart(file);

    try {
      const result = await uploadToAzure(file, { onProgress });
      const mediaUrl = result.url;

      if (!mediaUrl) {
        throw new Error('Upload completed but no URL returned');
      }

      // Handle SVG files specially
      if (file.type === 'image/svg+xml') {
        const reader = new FileReader();
        reader.onload = function(e) {
          const svgDataUrl = e.target.result;
          editor.chain().focus().setImage({ src: svgDataUrl }).run();
        };
        reader.readAsDataURL(file);
        
        if (onComplete) onComplete(result);
        return result;
      }

      // Insert image or video into editor
      if (file.type.startsWith('image/')) {
        const beforePos = editor.state.selection.from;
        editor.chain().focus().setImage({ src: mediaUrl }).run();
        // Move cursor below the inserted image
        setTimeout(() => {
          const { doc } = editor.state;
          let imagePos = -1;
          doc.descendants((node, pos) => {
            if (node.type.name === 'image' && node.attrs.src === mediaUrl && pos >= beforePos) {
              imagePos = pos;
              return false;
            }
          });
          if (imagePos >= 0) {
            // Move cursor to caption area (position after image node start, inside caption)
            const captionPos = imagePos + 1;
            editor.chain().focus().setTextSelection(captionPos).run();
          }
        }, 10);
      } else if (file.type.startsWith('video/')) {
        const beforePos = editor.state.selection.from;
        editor.chain().focus().insertContent({
          type: 'video',
          attrs: mergeBlockIndentAttrs(editor.state, {
            src: mediaUrl,
            controls: true,
            style: 'max-width:100%',
          }),
        }).run();
        // Move cursor below the inserted video
        setTimeout(() => {
          const { doc } = editor.state;
          let videoPos = -1;
          doc.descendants((node, pos) => {
            if (node.type.name === 'video' && node.attrs.src === mediaUrl && pos >= beforePos) {
              videoPos = pos;
              return false;
            }
          });
          if (videoPos >= 0) {
            // Move cursor to caption area (position after video node start, inside caption)
            const captionPos = videoPos + 1;
            editor.chain().focus().setTextSelection(captionPos).run();
          }
        }, 10);
      }

      if (onComplete) onComplete(result);
      return result;

    } catch (error) {
      console.error('Direct upload error:', error);
      if (onError) onError(error);
      throw error;
    }
  };
}

/**
 * Upload a download attachment directly to Azure
 * 
 * @param {File} file - The file to upload
 * @param {Object} options - Options
 * @returns {Promise<Object>} - { url, signed_id, filename, content_type }
 */
export async function uploadDownloadAttachment(file, options = {}) {
  return uploadToAzure(file, options);
}

export { DIRECT_UPLOAD_URL };

